/**
 * The image a donor posts after giving. Instagram has no web share intent, so an image handed to
 * the native share sheet (or downloaded) is the only way onto it; the other networks get it too.
 * Drawn on a canvas so it needs no dependency and no server.
 */

// The site's own logo (Navigation/Footer), as a PNG rendition. Cloudinary answers with
// `Access-Control-Allow-Origin: *`, so drawing it leaves the canvas exportable.
const LOGO_URL = 'https://res.cloudinary.com/brandpad/image/upload/c_scale,w_400,f_png/v1756758236/33917/logo_4b765b0f';

// The Certificate of Impact header gradient (DonationCertificateCard).
const BRAND_FROM = '#2d6a9f';
const BRAND_TO = '#4caf50';

// 4:5 portrait — the tallest ratio Instagram's feed keeps uncropped.
const WIDTH = 1080;
const HEIGHT = 1350;

interface TextBlock {
  readonly font: string;
  readonly text: string;
  readonly lineHeight: number;
  readonly gapAfter: number;
}

function loadImage(src: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

/** Greedy word wrap at the context's current font. */
function wrap(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const lines: string[] = [];
  let line = '';
  for (const word of text.split(' ')) {
    const candidate = line ? `${line} ${word}` : word;
    if (line && ctx.measureText(candidate).width > maxWidth) {
      lines.push(line);
      line = word;
    } else {
      line = candidate;
    }
  }
  if (line) lines.push(line);
  return lines;
}

/**
 * @param amountText - The formatted gift amount, only when the donor chose to show it
 * @returns A JPEG, or null when the browser cannot produce one
 */
export async function renderShareImage(amountText?: string): Promise<Blob | null> {
  const canvas = document.createElement('canvas');
  canvas.width = WIDTH;
  canvas.height = HEIGHT;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  // Canvas text does not wait for web fonts: without this the first render uses the fallback sans.
  await Promise.all(['400 40px Poppins', '500 44px Poppins', '600 40px Poppins', '700 76px Poppins', '800 120px Poppins']
    .map((font) => document.fonts.load(font))).catch(() => undefined);
  const logo = await loadImage(LOGO_URL);

  const background = ctx.createLinearGradient(0, 0, WIDTH, HEIGHT);
  background.addColorStop(0, BRAND_FROM);
  background.addColorStop(1, BRAND_TO);
  ctx.fillStyle = background;
  ctx.fillRect(0, 0, WIDTH, HEIGHT);

  const blocks: TextBlock[] = [
    { font: '500 44px Poppins, sans-serif', text: 'I just donated to', lineHeight: 64, gapAfter: 24 },
    { font: '700 76px Poppins, sans-serif', text: 'Pawsitive Future Foundation', lineHeight: 92, gapAfter: amountText ? 48 : 40 },
    ...(amountText ? [{ font: '800 120px Poppins, sans-serif', text: amountText, lineHeight: 132, gapAfter: 48 }] : []),
    { font: '400 40px Poppins, sans-serif', text: 'helping families afford veterinary care for their pets', lineHeight: 56, gapAfter: 0 }
  ];
  const wrapped = blocks.map((block) => {
    ctx.font = block.font;
    return { ...block, lines: wrap(ctx, block.text, WIDTH - 200) };
  });

  // Logo disc + text, centred together in the space above the footer.
  const discRadius = 130;
  const logoGap = 90;
  const footerTop = HEIGHT - 210;
  const textHeight = wrapped.reduce((sum, b) => sum + b.lines.length * b.lineHeight + b.gapAfter, 0);
  let y = Math.max(80, (footerTop - (discRadius * 2 + logoGap + textHeight)) / 2);

  const cx = WIDTH / 2;
  ctx.fillStyle = '#ffffff';
  ctx.beginPath();
  ctx.arc(cx, y + discRadius, discRadius, 0, Math.PI * 2);
  ctx.fill();
  if (logo) {
    const h = 176;
    const w = (h * logo.width) / logo.height;
    ctx.drawImage(logo, cx - w / 2, y + discRadius - h / 2, w, h);
  }
  y += discRadius * 2 + logoGap;

  ctx.fillStyle = '#ffffff';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';
  for (const block of wrapped) {
    ctx.font = block.font;
    for (const line of block.lines) {
      ctx.fillText(line, cx, y);
      y += block.lineHeight;
    }
    y += block.gapAfter;
  }

  ctx.font = '600 40px Poppins, sans-serif';
  ctx.fillText('pawsitivefuture.org/donate', cx, HEIGHT - 170);
  ctx.font = '400 34px Poppins, sans-serif';
  ctx.globalAlpha = 0.9;
  ctx.fillText('@pawsitivefuture', cx, HEIGHT - 110);
  ctx.globalAlpha = 1;

  // JPEG: a gradient PNG at this size is over 1MB, slow to hand to the share sheet on a phone.
  return new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.9));
}
